
import {
  PrayerRequest,
  PrayerStatus,
  PrayerType,
  SubmissionSource,
  VideoResponseStatus,
} from '@/types';
import { formatFullName } from '@/lib/format';
import { getPrayerCategories } from '@/lib/categories';
import { cn, formatRelativeTime } from '@/lib/utils';
import { Badge, Button, Card, getCategoryBadgeVariant } from '@/components/ui';
import type { BadgeVariant } from '@/components/ui';

const ROLES_CAN_MODERATE = ['moderator', 'producer', 'admin'];

export interface PrayerListProps {
  prayers: PrayerRequest[];
  isLoading: boolean;
  onEdit: (prayer: PrayerRequest) => void;
  onQueue: (prayer: PrayerRequest, queueType: 'prayer' | 'praise') => void;
  onDismiss: (prayer: PrayerRequest) => void;
  onReport?: (prayer: PrayerRequest) => void;
  onRestore?: (prayer: PrayerRequest) => void;
  /** Called when user clicks Respond (prayer partner queue page) */
  onRespond?: (prayer: PrayerRequest) => void;
  /** Moderator: send pending prayer to partners for video response */
  onRequestResponse?: (prayer: PrayerRequest) => void;
  /** Moderator: send a prayer to the stage tablet queue */
  onSendToStage?: (prayer: PrayerRequest) => void;
  /** submissions = moderation list; partner = prayer partner queue only */
  listContext?: 'submissions' | 'partner';
  /**
   * When false, hide request-response controls and response status badges.
   * Defaults to true when unset.
   */
  responsesEnabled?: boolean;
  /**
   * When true, show Send to Stage. Defaults to false when unset.
   */
  stageEnabled?: boolean;
  /** Whether current user can record video responses */
  canRespondVideo?: boolean;
  /** Whether current user can record audio responses */
  canRespondAudio?: boolean;
  /** Current user role for conditional button visibility (moderate, queue, etc.) */
  userRole?: string | null;
  selectedId?: string;
  /** Prayer ID -> { userId, userName } for who is currently editing (real-time) */
  editingBy?: Record<string, { userId: string; userName: string }>;
  /** Prayer ID -> { userId, userName } for who is currently responding (real-time) */
  respondingBy?: Record<string, { userId: string; userName: string }>;
  /** Current user ID so we can allow editing when the editor is self */
  currentUserId?: string | null;
  /** Prayer IDs currently being approved (disable Approve while in flight) */
  queuingIds?: ReadonlySet<string>;
  /** Prayer IDs currently being sent to stage */
  stagingIds?: ReadonlySet<string>;
}

const statusToVariant: Record<PrayerStatus, BadgeVariant> = {
  [PrayerStatus.PENDING]: 'warning',
  [PrayerStatus.QUEUED]: 'info',
  [PrayerStatus.DISPLAYED]: 'success',
  [PrayerStatus.ARCHIVED]: 'secondary',
  [PrayerStatus.RESPONDED]: 'emerald',
  [PrayerStatus.AWAITING_RESPONSE]: 'purple',
};

const statusLabels: Record<PrayerStatus, string> = {
  [PrayerStatus.PENDING]: 'Pending',
  [PrayerStatus.QUEUED]: 'Queued',
  [PrayerStatus.DISPLAYED]: 'Displayed',
  [PrayerStatus.ARCHIVED]: 'Dismissed',
  [PrayerStatus.RESPONDED]: 'Responded',
  [PrayerStatus.AWAITING_RESPONSE]: 'Awaiting Response',
};

const sourceIcons: Record<SubmissionSource, { icon: string; label: string }> = {
  [SubmissionSource.WEB]: { icon: '🌐', label: 'Web' },
  [SubmissionSource.SMS]: { icon: '💬', label: 'SMS' },
  [SubmissionSource.KIOSK]: { icon: '🖥️', label: 'Kiosk' },
};

function PrayerListSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3, 4, 5].map((i) => (
        <Card key={i} padding="sm" className="animate-pulse">
          <div className="flex items-start gap-4">
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-2">
                <div className="h-5 w-16 rounded bg-slate-200 dark:bg-slate-700" />
                <div className="h-5 w-20 rounded bg-slate-200 dark:bg-slate-700" />
              </div>
              <div className="h-4 w-3/4 rounded bg-slate-200 dark:bg-slate-700" />
              <div className="h-4 w-1/2 rounded bg-slate-200 dark:bg-slate-700" />
            </div>
            <div className="flex gap-2">
              <div className="h-8 w-8 rounded bg-slate-200 dark:bg-slate-700" />
              <div className="h-8 w-8 rounded bg-slate-200 dark:bg-slate-700" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

export function PrayerList({
  prayers,
  isLoading,
  onEdit,
  onQueue,
  onDismiss,
  onReport,
  onRestore,
  onRespond,
  onRequestResponse,
  onSendToStage,
  listContext = 'submissions',
  responsesEnabled = true,
  stageEnabled = false,
  canRespondVideo = false,
  canRespondAudio = false,
  userRole = null,
  selectedId,
  editingBy = {},
  respondingBy = {},
  currentUserId = null,
  queuingIds,
  stagingIds,
}: PrayerListProps) {
  const isModerator = ROLES_CAN_MODERATE.includes(userRole ?? '');

  const normalizePrayerTypeForUI = (t: PrayerType): PrayerType => {
    // Backend may send `prayer_request` / `praise_report`.
    // This list UI expects `prayer` / `praise` for correct badges/labels.
    if (t === PrayerType.PRAYER_REQUEST) return PrayerType.PRAYER;
    if (t === PrayerType.PRAISE_REPORT) return PrayerType.PRAISE;
    return t;
  };

  if (isLoading) {
    return <PrayerListSkeleton />;
  }

  if (prayers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="mb-4 rounded-full bg-slate-100 p-4 dark:bg-slate-700">
          <svg
            className="h-8 w-8 text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-slate-900 dark:text-white">
          No submissions found
        </h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Try adjusting your filters or check back later for new submissions.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" role="list" aria-label="Submissions list">
      {prayers.map((prayer) => {
        const uiType = normalizePrayerTypeForUI(prayer.type);
        const statusVariant =
          prayer.status && statusToVariant[prayer.status] != null
            ? statusToVariant[prayer.status]
            : 'warning';
        const statusLabel =
          prayer.status && statusLabels[prayer.status] != null
            ? statusLabels[prayer.status]
            : 'Pending';
        const responseAwaiting =
          prayer.videoResponseStatus === VideoResponseStatus.AWAITING ||
          prayer.status === PrayerStatus.AWAITING_RESPONSE;
        const responseResponded =
          prayer.videoResponseStatus === VideoResponseStatus.RESPONDED ||
          prayer.status === PrayerStatus.RESPONDED;
        const prayerCategories = getPrayerCategories(prayer);
        const source = sourceIcons[prayer.submittedVia];
        const isSelected = selectedId === prayer.id;
        const canApproveToQueue =
          prayer.status === PrayerStatus.PENDING ||
          prayer.status === PrayerStatus.AWAITING_RESPONSE ||
          prayer.status === PrayerStatus.RESPONDED;

        return (
          <Card
            key={prayer.id}
            role="listitem"
            padding="sm"
            className={cn(
              'group relative transition-all',
              isSelected
                ? 'border-primary-500 ring-2 ring-primary-500/20'
                : 'hover:border-slate-300 dark:hover:border-slate-600',
              'hover:shadow-md'
            )}
          >
            {/* Header */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {/* Status badge */}
              <Badge variant={statusVariant}>{statusLabel}</Badge>

              {responsesEnabled && listContext === 'submissions' && responseAwaiting && (
                <Badge variant="purple">Awaiting Response</Badge>
              )}
              {responsesEnabled && listContext === 'submissions' && responseResponded && (
                <Badge variant="emerald">Responded</Badge>
              )}

              {/* Type badge */}
              <Badge variant={uiType === PrayerType.PRAYER ? 'sky' : 'amber'}>
                {uiType === PrayerType.PRAYER ? 'Prayer' : 'Answered Prayer'}
              </Badge>

              {/* Category badges */}
              {prayerCategories.map((cat) => (
                <Badge
                  key={cat}
                  variant={getCategoryBadgeVariant(cat)}
                  className="capitalize"
                >
                  {cat}
                </Badge>
              ))}

              {/* Source indicator */}
              <span
                className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400"
                title={`Submitted via ${source.label}`}
              >
                <span>{source.icon}</span>
                <span className="hidden sm:inline">{source.label}</span>
              </span>

              {/* Anonymous indicator */}
              {prayer.isAnonymous && (
                <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                  <svg
                    className="h-3.5 w-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  Anonymous
                </span>
              )}

              {/* Report inappropriate (small flag icon) + Timestamp */}
              <div className="ml-auto flex items-center gap-2">
                {listContext === 'submissions' &&
                  ROLES_CAN_MODERATE.includes(userRole ?? '') &&
                  onReport && (
                    <button
                      type="button"
                      onClick={() => onReport(prayer)}
                      className="rounded p-1 text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
                      aria-label={
                        (prayer.reportCount ?? 0) > 0
                          ? `Reported ${prayer.reportCount} time${prayer.reportCount === 1 ? '' : 's'}; report again`
                          : 'Report inappropriate content'
                      }
                      title={
                        (prayer.reportCount ?? 0) > 0
                          ? `Reported ${prayer.reportCount} time${prayer.reportCount === 1 ? '' : 's'}`
                          : 'Report inappropriate'
                      }
                    >
                      <svg
                        className="h-4 w-4"
                        fill={(prayer.reportCount ?? 0) > 0 ? 'currentColor' : 'none'}
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2v3"
                        />
                      </svg>
                    </button>
                  )}
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {formatRelativeTime(prayer.createdAt)}
                </span>
              </div>
            </div>

            {/* Body */}
            <p className="mb-3 text-sm text-slate-700 dark:text-slate-300 line-clamp-3">
              {prayer.body}
            </p>

            {/* Submitter info */}
            {!prayer.isAnonymous && (prayer.firstName || prayer.lastName) && (
              <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
                From: <span className="font-medium">{formatFullName(prayer.firstName, prayer.lastName)}</span>
                {prayer.location && <span> • {prayer.location}</span>}
              </p>
            )}

            {/* Editing by another user */}
            {editingBy[prayer.id] && (
              <p className="mb-2 text-xs text-amber-600 dark:text-amber-400 font-medium">
                Currently being edited by {editingBy[prayer.id]?.userName}
              </p>
            )}

            {/* Responding by another user */}
            {respondingBy[prayer.id] && (
              <p className="mb-2 text-xs text-amber-600 dark:text-amber-400 font-medium">
                Currently being responded to by {respondingBy[prayer.id]?.userName}
              </p>
            )}

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-700">
              {/* Edit button - moderator+ only, disabled when another user is editing */}
              {listContext === 'submissions' && ROLES_CAN_MODERATE.includes(userRole ?? '') && (() => {
                const editor = editingBy[prayer.id];
                const isEditedByOther = editor && editor.userId !== currentUserId;
                return (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(prayer)}
                className="h-8"
                disabled={!!isEditedByOther}
                aria-label={isEditedByOther ? `Being edited by ${editor.userName}` : `Edit prayer request from ${formatFullName(prayer.firstName, prayer.lastName) || 'Anonymous'}`}
              >
                <svg
                  className="mr-1.5 h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Edit
              </Button>
                );
              })()}

              {/* Restore button - for dismissed prayers, moderator+ only */}
              {listContext === 'submissions' && ROLES_CAN_MODERATE.includes(userRole ?? '') && prayer.status === PrayerStatus.ARCHIVED && onRestore && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRestore(prayer)}
                  className="h-8 text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
                  aria-label="Restore prayer request"
                >
                  <svg
                    className="mr-1.5 h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Restore
                </Button>
              )}

              {/* Spacer */}
              <div className="flex-1" />

              {/* Moderator: request partner response / state indicators (submissions only) */}
              {responsesEnabled &&
                listContext === 'submissions' &&
                isModerator &&
                (prayer.email || prayer.phone?.trim()) &&
                prayer.status !== PrayerStatus.ARCHIVED &&
                (() => {
                  if (responseAwaiting) {
                    return (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled
                        className="h-8 cursor-default text-slate-500 opacity-80 dark:text-slate-400"
                        aria-label="Video prayer requested; awaiting partner response"
                      >
                        Requested
                      </Button>
                    );
                  }
                  if (responseResponded) {
                    return (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled
                        className="h-8 cursor-default text-emerald-700/80 opacity-90 dark:text-emerald-400/90"
                        aria-label="Prayer partner has responded"
                      >
                        Responded
                      </Button>
                    );
                  }
                  if (onRequestResponse) {
                    return (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRequestResponse(prayer)}
                        className="h-8 text-violet-600 hover:text-violet-700 hover:bg-violet-50 dark:text-violet-400 dark:hover:bg-violet-900/20"
                        aria-label="Request response from partners"
                      >
                        <svg
                          className="mr-1.5 h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                          />
                        </svg>
                        Request Response
                      </Button>
                    );
                  }
                  return null;
                })()}

              {/* Partner queue: Respond */}
              {listContext === 'partner' &&
                onRespond &&
                (prayer.email || prayer.phone?.trim()) &&
                responseAwaiting &&
                (() => {
                  const responder = respondingBy[prayer.id];
                  const isRespondedByOther = responder && responder.userId !== currentUserId;
                  return (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRespond(prayer)}
                      className="h-8"
                      disabled={!!isRespondedByOther}
                      aria-label={
                        isRespondedByOther
                          ? `Being responded to by ${responder.userName}`
                          : 'Respond to prayer request'
                      }
                    >
                      <svg
                        className="mr-1.5 h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                        />
                      </svg>
                      Respond
                    </Button>
                  );
                })()}

              {/* Send to Stage - moderator+ only, left of Approve */}
              {stageEnabled &&
                listContext === 'submissions' &&
                ROLES_CAN_MODERATE.includes(userRole ?? '') &&
                prayer.status !== PrayerStatus.ARCHIVED &&
                (prayer.onStage ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled
                    className="h-8 cursor-default text-slate-500 opacity-80 dark:text-slate-400"
                    aria-label="Already on the stage queue"
                  >
                    On Stage
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onSendToStage?.(prayer)}
                    disabled={stagingIds?.has(prayer.id) || !onSendToStage}
                    className="h-8 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/20"
                    aria-label="Send prayer to the stage tablet"
                  >
                    <svg
                      className="mr-1.5 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 19h16M6 19V9h12v10M9 9V6h6v3"
                      />
                    </svg>
                    Send to Stage
                  </Button>
                ))}

              {/* Approve button - moderator+ only: pending, video-requested (awaiting_response), or responded */}
              {listContext === 'submissions' && ROLES_CAN_MODERATE.includes(userRole ?? '') && canApproveToQueue && (() => {
                const queueType: 'prayer' | 'praise' =
                  prayer.type === PrayerType.PRAISE_REPORT || prayer.type === PrayerType.PRAISE
                    ? 'praise'
                    : 'prayer';
                const queueLabel = queueType === 'praise' ? 'praise queue' : 'prayer queue';
                return (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onQueue(prayer, queueType)}
                    disabled={queuingIds?.has(prayer.id)}
                    className={cn(
                      queueType === 'praise'
                        ? 'h-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20'
                        : 'h-8 text-sky-600 hover:text-sky-700 hover:bg-sky-50 dark:text-sky-400 dark:hover:bg-sky-900/20'
                    )}
                    aria-label={`Approve and add to ${queueLabel}`}
                  >
                    <svg
                      className="mr-1.5 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Approve
                  </Button>
                );
              })()}

              {/* Dismiss button - moderator+ only */}
              {listContext === 'submissions' && ROLES_CAN_MODERATE.includes(userRole ?? '') && prayer.status !== PrayerStatus.ARCHIVED && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDismiss(prayer)}
                  className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                  aria-label="Dismiss prayer request"
                >
                  <svg
                    className="mr-1.5 h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Dismiss
                </Button>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
